import logo from "assets/images/integrations/newsletter2go_logo.png";
import { Map } from "immutable";
import { useCallback, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import { deleteIntegrationAction, saveIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { Newsletter2GoSettingsForm } from "./SettingsForm";
import { Newsletter2GoIntegrationSettings, Newsletter2GoListsSettings } from "./types";

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  const integrationList = state
    .getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data",
      IntegrationType.NEWSLETTER_2_GO
    ])
    ?.toJS() as Newsletter2GoIntegrationSettings[] | undefined;

  const connectedIntegration = integrationList?.find((integration) => {
    return integration.oath2Config && integration.oath2Config.access_token;
  });

  const isDeleting = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "deletingItem",
        IntegrationType.NEWSLETTER_2_GO,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  const isSaving = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "savingItem",
        IntegrationType.NEWSLETTER_2_GO,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  return {
    selectedShop,
    connectedIntegration,
    isDeleting,
    isSaving
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      deleteIntegrationAction,
      saveIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type Newsletter2GoSettingsViewComponentProps = ConnectedProps<typeof connector>;

const Newsletter2GoSettingsViewComponent = (props: Newsletter2GoSettingsViewComponentProps) => {
  const {
    connectedIntegration,
    isDeleting,
    selectedShop,
    deleteIntegrationAction,
    isSaving,
    saveIntegrationAction
  } = props;

  const handleDelete = useCallback(() => {
    if (connectedIntegration) {
      deleteIntegrationAction(
        selectedShop,
        IntegrationType.NEWSLETTER_2_GO,
        connectedIntegration.id
      );
    }
  }, [connectedIntegration, selectedShop]);

  const handleSave = useCallback(
    (values: Newsletter2GoListsSettings) => {
      const duplicateApp = { ...connectedIntegration };
      Object.assign(duplicateApp, { settings: values });

      saveIntegrationAction(selectedShop, IntegrationType.NEWSLETTER_2_GO, duplicateApp);
    },
    [connectedIntegration, selectedShop]
  );

  const listOptions = useMemo(
    () => connectedIntegration?.info?.lists || [],
    [connectedIntegration?.info?.lists]
  );

  const initialValues = useMemo(
    () => Map(connectedIntegration?.settings || {}),
    [connectedIntegration?.settings]
  );

  if (!connectedIntegration) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={connectedIntegration.info?.accountName}
      deleting={isDeleting}
      image={logo}
      name="Newsletter2Go"
      onDelete={handleDelete}
    >
      <Newsletter2GoSettingsForm
        listOptions={listOptions}
        initialValues={initialValues}
        saving={isSaving}
        handleSave={handleSave}
      />
    </ConnectedIntegrationLineWrapper>
  );
};

export const Newsletter2GoSettingsView = connector(Newsletter2GoSettingsViewComponent);
